/**
 * Empire-PWA chassis · WebAuthn passkey enrollment + assertion.
 * See almari/src/chassis/passkey.ts for the architecture rationale.
 */
import type { Passcode } from './branded.ts';

const RP_NAME = 'Dial';
const RP_ID = location.hostname || 'dial.yourtube.in';
const DB_NAME = 'dial-passkey';
const DB_STORE = 'cache';

async function db(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = (): void => {
      req.result.createObjectStore(DB_STORE);
    };
    req.onerror = (): void => reject(req.error);
    req.onsuccess = (): void => resolve(req.result);
  });
}

async function dbGet(key: string): Promise<unknown> {
  const d = await db();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(DB_STORE, 'readonly').objectStore(DB_STORE).get(key);
    tx.onsuccess = (): void => resolve(tx.result);
    tx.onerror = (): void => reject(tx.error);
  });
}

async function dbSet(key: string, value: unknown): Promise<void> {
  const d = await db();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(DB_STORE, 'readwrite').objectStore(DB_STORE).put(value, key);
    tx.onsuccess = (): void => resolve();
    tx.onerror = (): void => reject(tx.error);
  });
}

export function isAvailable(): boolean {
  return typeof PublicKeyCredential !== 'undefined' && typeof navigator.credentials?.create === 'function';
}

export async function enroll(passcode: Passcode, displayName: string): Promise<{ credentialId: string }> {
  if (!isAvailable()) throw new Error('WebAuthn unavailable');
  const userId = crypto.getRandomValues(new Uint8Array(16));
  const challenge = crypto.getRandomValues(new Uint8Array(32));

  const cred = await navigator.credentials.create({
    publicKey: {
      challenge,
      rp: { id: RP_ID, name: RP_NAME },
      user: { id: userId, name: displayName, displayName },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: {
        userVerification: 'required',
        residentKey: 'required',
        authenticatorAttachment: 'platform',
      },
      timeout: 60_000,
      attestation: 'none',
    },
  }) as PublicKeyCredential | null;

  if (!cred) throw new Error('passkey creation cancelled');
  const credentialId = b64url(new Uint8Array(cred.rawId));

  const keyMat = await crypto.subtle.digest('SHA-256', cred.rawId);
  const aesKey = await crypto.subtle.importKey('raw', keyMat, { name: 'AES-GCM', length: 256 }, false, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, new TextEncoder().encode(passcode));
  await dbSet(credentialId, { iv: Array.from(iv), ct: Array.from(new Uint8Array(ct)) });

  return { credentialId };
}

export async function assertAndUnlock(): Promise<Passcode | null> {
  if (!isAvailable()) return null;
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  try {
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        rpId: RP_ID,
        userVerification: 'required',
        timeout: 60_000,
      },
      mediation: 'optional',
    }) as PublicKeyCredential | null;
    if (!assertion) return null;
    const credentialId = b64url(new Uint8Array(assertion.rawId));
    const blob = await dbGet(credentialId) as { iv: number[]; ct: number[] } | undefined;
    if (!blob) return null;
    const keyMat = await crypto.subtle.digest('SHA-256', assertion.rawId);
    const aesKey = await crypto.subtle.importKey('raw', keyMat, { name: 'AES-GCM', length: 256 }, false, ['decrypt']);
    const pt = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: new Uint8Array(blob.iv) },
      aesKey,
      new Uint8Array(blob.ct),
    );
    return new TextDecoder().decode(pt) as Passcode;
  } catch {
    return null;
  }
}

function b64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
